import moment from "moment";
import { AccountDetails, LoginInfo, MembershipStatus, MFAType, PaymentOrderAttachment, PaymentItemResponse, PermissionActionCode, PermissionGroup, PermissionGroupId, PermissionGroups, PermissionLevelId, PermissionReqSource, ServiceCategory, VerificationCodeType, PaymentMethodCategory, PaymentMethodTypes, ContactCategory, PaymentLogChangeKey, SimplifiedAccountDetails, Language, CPDContentDictionary, CPDQuestion, CPDQuestionOption, Invoice, PaymentItemLogResponse, PaymentCategory, CPDCategory, EmptyLanguageSet, LanagugeContentSet, CPDActivity, ModalityStatus } from "../constants/options";
import { JOB_CATEGORY, LATEST_LOGIN_VERSION, USER_INFO } from "../constants/storage-keys";
import { GetLocalStorage } from "./cryptography";
import { isWeb } from "./platform";
import { ContactInfoOption } from "../components/profile-page/basic-info/update-contact-info";
import { MemberService } from "../components/profile-page/basic-info/sections/services-provided-section";
import { isDevelopment, REMOVE_CPD_ADD_ON } from "../constants/settings";
import { IsNullOrUndefined } from "./field-validation";
import { ReportError } from "./error-reporting";
import useStores from "../hooks/use-stores";
import { ClinicInfo } from "../components/profile-page/clinic-details/single-clinic-info";
import { t } from "i18next";

export const IsAuthed = () => {
  return (
    isWeb && localStorage.USER_TOKEN != null && localStorage.USER_INFO != null
  );
};

export const IsSystemInited = () => {
  return GetLocalStorage(JOB_CATEGORY) != null;
};

export const GetUserProfile = () => {
  if (isWeb && localStorage.USER_INFO) return GetLocalStorage(USER_INFO) as LoginInfo;
  // return GetLocalStorage(USER_INFO) as LoginInfo;
};

export const ConvertListToCommonOptions = (
  infoList: any[],
  fieldId: string,
  fieldLabel: string,
  extraField?: string
) => {
  // @ts-ignore
  return infoList.map((info: any) => ({
    id: info[fieldId],
    label: info[fieldLabel],
    extraField: info[extraField as keyof typeof info],
  }));
};

export const GetException = (exceptionStr: string) => {
  return exceptionStr.split("Error:")[exceptionStr.split("Error:").length - 1];
};

export const IsDesktop = isWeb ? window.innerWidth > 1536 : false;
export const Resident_Category_Id = 3;

// Time
export const CNDateTimeFormat = "YYYY-MM-DD HH:mm";
export const CNDateFormat = "YYYY-MM-DD";
export const AUDateTimeFormat = "DD/MM/YYYY hh:mm A";
export const AUDMonthYearFormat = "MMM YYYY";
export const AUTimeFormat = "hh:mm A";
export const AUDDateFormat = "DD/MM/YYYY";
export const AUDTimeFormat = "hh:mm A";
export const AUStrDateTimeFormat = "DD MMM YYYY HH:mm A";
export const AUStrFullDateTimeFormat = "ddd, DD MMM YYYY HH:mm A";
export const AUStrDateFormat = "DD MMM YYYY";
export const AUStrDateTimeNonYearFormat = "DD MMM HH:mm A";
export const ProcessTimeStr = (time: string) => {
  return `${moment(time).isSame(moment(), "day")
    ? `Today ${moment(time).format(AUDTimeFormat)}`
    : moment(time).format(AUDateTimeFormat)
    }`;
};

/**
 * Determines whether the supplied date falls within Australian Eastern Daylight
 * Time (AEDT, UTC+11). Outside this range Australian Eastern Standard Time
 * (AEST, UTC+10) applies.
 *
 * Australian Eastern DST rules (NSW/VIC/ACT/TAS):
 *  - Starts: first Sunday of October at 02:00
 *  - Ends:   first Sunday of April at 03:00
 *
 * The date is interpreted using the same local wall-clock that all our time
 * formatters use (moment local), so the label always matches what is displayed.
 */
export const IsAEDT = (date: string | Date | moment.Moment | null | undefined): boolean => {
  if (!date) return false;
  const m = moment(date);
  if (!m.isValid()) return false;

  const year = m.year();

  // First Sunday of October @ 02:00 (DST begins -> AEDT)
  const dstStart = moment([year, 9, 1, 2, 0]);
  dstStart.date(1 + ((7 - dstStart.day()) % 7));

  // First Sunday of April @ 03:00 (DST ends -> AEST)
  const dstEnd = moment([year, 3, 1, 3, 0]);
  dstEnd.date(1 + ((7 - dstEnd.day()) % 7));

  // AEDT spans the summer across the new year boundary: from the October start
  // through to the following April end.
  return m.isSameOrAfter(dstStart) || m.isBefore(dstEnd);
};

/**
 * Returns the correct Australian Eastern timezone label ("AEDT" or "AEST") for
 * the supplied date based on whether daylight saving is in effect.
 */
export const GetAUTimezoneLabel = (date: string | Date | moment.Moment | null | undefined): string => {
  return IsAEDT(date) ? "AEDT" : "AEST";
};

/**
 * Resolves the timezone labels for a pair of times (e.g. a start/end range).
 * Returns a tuple of [startLabel, endLabel]:
 *  - If both times share the same timezone, the label is shown only on the
 *    later time (endLabel) and startLabel is an empty string.
 *  - If they differ (the range crosses the DST boundary), each time carries its
 *    own label.
 */
export const GetAUTimezoneLabelPair = (
  start: string | Date | moment.Moment | null | undefined,
  end: string | Date | moment.Moment | null | undefined
): [string, string] => {
  const startLabel = GetAUTimezoneLabel(start);
  const endLabel = GetAUTimezoneLabel(end);
  if (startLabel === endLabel) return ["", endLabel];
  return [startLabel, endLabel];
};

// Time tokens (hour / minute / second / meridiem). Month is "M" (uppercase),
// minute is "m" (lowercase) - they don't collide.
const TIME_TOKEN_REGEX = /[HhmsAa]/;

/** Whether a moment format string includes any time-of-day component. */
export const FormatHasTime = (format: string): boolean => TIME_TOKEN_REGEX.test(format);

/**
 * Formats a date and appends the appropriate Australian Eastern timezone label
 * (AEDT/AEST). The label is ONLY appended when the chosen format actually
 * renders a time component - date-only formats are returned without a label.
 */
export const FormatAUDateTime = (
  date: string | Date | moment.Moment | null | undefined,
  format: string = AUStrDateTimeFormat,
  withTimezone: boolean = true
): string => {
  if (!date) return "--";
  const m = moment(date);
  if (!m.isValid()) return "--";

  const formatted = m.format(format);
  if (withTimezone && FormatHasTime(format)) {
    return `${formatted} ${GetAUTimezoneLabel(m)}`;
  }
  return formatted;
};

export const AlphabetList: string[] = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

//unitNo and address
export const GetAddress = (addressLine: string) => {
  let unitNo = "";
  let street = "";
  if (addressLine) {
    [unitNo, street] = addressLine.split(/\/(.+)/);
  }
  return {
    unitNo,
    street,
  };
};

// export const GetErrorInfoWithLog = (err: any) => {
//   const log = GetErrorInfo(err);
//   console.log(log);
//   return log;
// }


/**
 * Requests sent with `responseType: 'blob' | 'arraybuffer'` receive their JSON
 * error bodies as binary too, which hides `message` from GetErrorInfo and makes
 * every failure read as "Unknown Error". Decode those back into a plain object
 * so the normal extraction path below can find the real message.
 *
 * Called from the Axios / FAxios response interceptors, where awaiting is
 * possible - GetErrorInfo itself is synchronous and cannot read a Blob.
 */
export const NormalizeBinaryErrorResponse = async (error: any) => {
  const data = error?.response?.data;
  if (!data) return;

  try {
    if (data instanceof Blob) {
      error.response.data = JSON.parse(await data.text());
    } else if (data instanceof ArrayBuffer) {
      error.response.data = JSON.parse(new TextDecoder("utf-8").decode(data));
    }
  } catch {
    // NOT A JSON ERROR BODY (E.G. AN HTML GATEWAY PAGE) - LEAVE IT UNTOUCHED
  }
};

export const GetErrorInfo = (err: any, defaultMessage: string = "Unknown Error") => {//err is err:obj
  // if (err.message) { console.log(err.message); }
  // if (err.response) { console.log(err.response); }
  // console.log(DeepCopy(
  //   {
  //     ...DeepCopy(err),
  //     currentUrl: isWeb ? window.location.href : "",
  //     userInfo: {
  //       GetUserProfile()?.id
  //     } || "Unknown",
  //     requestBody: err?.config?.data || "Unknown",
  //     callStack: err?.stack || "Unknown",
  //     message: err.message,
  //     response: err.response,
  //     name: err.name,
  //     status: err.status,
  //     statusText: err.statusText,
  //     config: err.config,
  //     request: err.request,
  //   }
  // ));

  console.log("err", typeof err, err);

  if (err === "ERR_CANCELED") return "";

  // .NET hides the real cause behind "See the inner exception for details", so the
  // text we would otherwise show the user is useless. Ship the details to the error
  // collector instead and tell the user to reach out. Fire-and-forget - reporting
  // never throws and never blocks the string we hand back.
  const messageText = typeof err === "string"
    ? err
    : `${err?.message ?? ""} ${err?.response?.data?.message ?? ""}`;

  if (messageText.toLowerCase().includes("inner exception")) {
    ReportError({
      user: GetUserProfile(),
      errorLog: messageText.trim(),
      backendEndpoint: err?.config?.url ?? "",
      additionalPayload: {
        name: err?.name,
        message: err?.message,
        status: err?.response?.status,
        method: err?.config?.method,
        requestBody: err?.config?.data,
        responseMessage: err?.response?.data?.message,
      },
    });

    return "Unknown error. Please contact the developers.";
  }

  if (typeof err === "string") {
    if (err.includes("Network error! Check the connection first.")) {
      return "Failed to connect to server";
    }
    return err;
  }

  // An AxiosError carries no `response` at all on timeout / DNS failure / CORS
  // rejection / cancellation, so switch on the status itself rather than on the
  // error name - a non-Axios error simply has no status to match.
  const status = err?.response?.status;

  if ((err?.message || err) === "ERR_CANCELED") {
    // DO NOTHING, THIS IS A CANCELLED REQUEST
    return "";
  } else if (err?.message?.includes("Network error!")) {
    return "Failed to connect to server.";
  } else if (status === 400) {
    return "Mis-formatted request. Please check the request format.";
  } else if (status === 401) {
    // Suppressed: the global Axios interceptor already shows the session-expiry warning.
    // The login page handles its own credential errors without calling this.
    return "";
  } else if (status === 403) {
    return "You are not authorized to access certain information.";
  } else if (status === 404) {
    return "Failed to find the requested resource.";
  } else if (status === 405) {
    return "The request method is not allowed.";
  } else if (err?.name === "TypeError") {
    return `Error: ${err.message}`;
  }

  if (("" + err).includes("Network error! Check the connection first.")) {
    return "Failed to connect to server";
  }

  // BLOB / ARRAYBUFFER BODIES ARE ALREADY DECODED BY NormalizeBinaryErrorResponse
  if (!err?.response) return defaultMessage;

  let input = err.response.data?.message;
  const regex = /Error:/g;
  let match: RegExpExecArray | null;
  let lastMatchIndex = -1;

  while ((match = regex.exec(input)) !== null) {
    lastMatchIndex = match.index;
  }

  if (lastMatchIndex !== -1) {
    const message = input.substring(lastMatchIndex + "Error:".length).trim();

    // if (message.toLowerCase().includes("sequence contains no elements")) return defaultMessage;
    // if (message.toLowerCase().includes("")) return defaultMessage;
    return message;
  }
  if (err.response.status === 403) {
    return "No Permission";
  }

  return defaultMessage;

};

export const IsNotExpired = (dataMaps: any[]) => {
  return dataMaps.some((dataMap) => {
    const expireMoment = moment(dataMap.expireDate);
    return expireMoment.isAfter(moment());
  });
  return false;
};

export enum SystemSettingTab {
  AGENT_INFO,
  ACCOUNT,
  EMAIL,
  SYNC,
  AREA,
}

/**
 * Priority
 */
export enum Priority {
  LOW = "Low",
  MEDIUM = "Medium",
  HIGH = "High",
}

export const IsAll: number = 999;

export const ScrolledToBottom = (listElement: HTMLElement, childElement: HTMLElement) => {
  if (!listElement) return false;
  if (!childElement) return false;
  const threshold = 50;

  return childElement.getBoundingClientRect().bottom - listElement.getBoundingClientRect().bottom < threshold;
};

export const GetOptionText = (options: any[], value: any, language: Language | undefined = undefined) => {
  if (!value) return "--";
  if (!options || options.length === 0) return "--";
  const option = options.find((item: any) => item.value === value || item.id === value);

  if (language && option) {
    if (language === Language.ENGLISH) return option.enName ?? option.cnName ?? option.label ?? option.name ?? "--";
    if (language === Language.ZH_CN) return option.cnName ?? option.enName ?? option.label ?? option.name ?? "--";
  };

  if (option) return option.label ?? option.name ?? option.enName ?? option.cnName ?? "--";
  return "--";
}

export const DeepCopy = (oldObject: any) => {
  if (oldObject === null || oldObject === undefined) return null;

  // return structuredClone(oldObject);
  return JSON.parse(JSON.stringify(oldObject));
};

export const CopyToClipboard = (text: string) => {
  if (!text) return;
  navigator.clipboard.writeText(text);
}

export const GetObscuredMobile = (mobile: string) => {
  if (!mobile) return "--";
  return `*********${mobile && mobile.substring(mobile.length - 3)}`;
}

export const GetObscuredEmail = (email: string) => {
  if (!email) return "--";
  return `${email.substring(0, 3)}*****@${email.split("@")[1]}`;
}


export const MfaTypeToVerificationCodeType = (mfaType: MFAType) => {
  switch (mfaType) {
    case MFAType.SMS: return VerificationCodeType.SMS;
    case MFAType.EMAIL: return VerificationCodeType.EMAIL;
    case MFAType.QR_CODE: return VerificationCodeType.APP;
    default: return null;
  }
}

export const GetShortenName = (name: string, maxLength: number = 20) => {
  if (!name) return "--";
  return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
}


export const getContactInfoOptionText = (mode: ContactInfoOption) => {
  switch (mode) {
    case ContactInfoOption.MOBILE_NUMBER:
      return "mobile number";
    case ContactInfoOption.PHONE_NUMBER:
      return "phone number";
    case ContactInfoOption.EMAIL_ADDRESS:
      return "email address";
    default:
      return "--";
  }
}

export const GetMemberFullName = (personalInfo: AccountDetails | SimplifiedAccountDetails | null | undefined, withId: boolean = false, withRole: boolean = false) => {
  if (!personalInfo) return "--";
  if ((personalInfo as SimplifiedAccountDetails).isFeLoading) return "loading...";
  let name = personalInfo.firstName || "-";
  if (personalInfo.lastName) name += " " + personalInfo.lastName;

  name = GetShortenName(name, 25);

  if (personalInfo.chineseFirstName || personalInfo.chineseLastName) {
    name += " (";
    if (personalInfo.chineseLastName) name += personalInfo.chineseLastName;
    if (personalInfo.chineseLastName && personalInfo.chineseFirstName) name += " ";
    if (personalInfo.chineseFirstName) name += personalInfo.chineseFirstName;
    name += ")";
  }

  if (withId) name += ` [#${personalInfo.cMemberId || personalInfo.id || 0}]`;
  if (withRole) {
    switch ((personalInfo as AccountDetails).contactCategoryId) {
      case ContactCategory.MEMBER:
        name += ` (Member)`;
        break;
      case ContactCategory.SYSTEM:
        name += ` (System)`;
        break;
      case ContactCategory.ADMIN:
        name += ` (Admin)`;
        break;
      case ContactCategory.SUPER_ADMIN:
        name += ` (Super Admin)`;
        break;
    }
  }

  return name;
}

export const ExtractMemberServices = (service: MemberService[]) => {
  let tempServices: ModalityStatus = {
    ACU: false,
    REM: false,
    CHM: false,
    MAS: false,
    TCH: false,
  };
  service
    .filter((item: any) => !item.isDeleted)
    .map((item: any) => {
      if (item.servicesCategoryId === ServiceCategory.ACU) tempServices.ACU = true;
      else if (item.servicesCategoryId === ServiceCategory.REM) tempServices.REM = true;
      else if (item.servicesCategoryId === ServiceCategory.CHM) tempServices.CHM = true;
      else if (item.servicesCategoryId === ServiceCategory.MAS) tempServices.MAS = true;
      else if (item.servicesCategoryId === ServiceCategory.TCH) tempServices.TCH = true;
    })

  return tempServices
}

export const IsPaidMember = (membershipStatusId: MembershipStatus | null | undefined) => {
  if (!membershipStatusId) return false;
  return ![MembershipStatus.FREE, MembershipStatus.DRAFT].includes(membershipStatusId);
}

export const getTextHash = async (text: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export const CountBase64ImagesInHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const images = doc.getElementsByTagName('img');
  return Array.from(images).filter((img) => img.src.startsWith('data:image')).length;
}

export const ReplaceBase64ImagesInHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const images = doc.getElementsByTagName('img');

  Promise.all(Array.from(images).map((img) => {
    if (img.src.startsWith('data:image')) {
      img.src = "";
      img.alt = 'Image failed to upload';
    }
  }));

  return doc.body.innerHTML;
}

export const ExtractAndUploadB64Images = async (htmlContent: string, uploadFunction: (imgB64String: string) => Promise<string>, uploadedHashCache: Map<string, string>): Promise<string> => {
  `For extracting base64 images from rich text content and uploading them to the server. `

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const images = doc.getElementsByTagName('img');
  const b64Images = Array.from(images).filter((img) => img.src.startsWith('data:image'));

  // find unique images and upload them
  const uniqueHash = new Set<string>([...b64Images.map((img) => img.src.split(',')[1])]);

  // UPLOAD ALL IN ONE GO
  const uploadList = [...uniqueHash].map(async (imgB64String) => {
    const newHash = await getTextHash(imgB64String);
    if (!uploadedHashCache.has(newHash)) {
      const uploadUrl = await uploadFunction(imgB64String);
      uploadedHashCache.set(newHash, uploadUrl);
    }
  });
  await Promise.all(uploadList);

  // Modify each image's src attribute
  await Promise.all(b64Images.map(async (img) => {
    if (img.src.startsWith('data:image')) {
      const base64String = img.src.split(',')[1];
      const newHash = await getTextHash(base64String);

      const existingImage = uploadedHashCache.get(newHash);

      if (existingImage) {
        img.src = existingImage;
      } else {
        // Generate a temporary URL for reference
        const uploadUrl = await uploadFunction(base64String);
        // Store the image data
        uploadedHashCache.set(newHash, uploadUrl);
        img.src = uploadUrl;
      }
    }
  }));

  return doc.body.innerHTML;
}

export const RemoveImageHeight = (htmlContent: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const images = doc.getElementsByTagName('img');

  Array.from(images).map((img) => {
    img.removeAttribute('height');
  });

  return doc.body.innerHTML;
}

export const GenerateYearOptions = (startYear: number, endYear: number) => {
  return Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i).map((year) => ({ id: year, label: "" + year, value: year }));
}

export const HasPermission = (action: PermissionActionCode, pmsGroup: PermissionGroupId, path?: string) => {
  let group: PermissionGroup | any = null;

  // if (path) {
  //   if (path.includes("dashboard")) return true;
  //   else if (path.includes("inspection-list")) return true;
  //   else group = PermissionGroups.find((pg: PermissionGroup) => pg.pagePaths.filter((pp: any) => path.includes(pp.path)).length > 0);
  // } else group = PermissionGroups.find((pg: PermissionGroup) => pg.id == pmsGroup);

  group = PermissionGroups.find((pg: PermissionGroup) => pg.id == pmsGroup);

  const permissions = GetUserProfile()?.contact?.pc;
  if (group && permissions) {
    let targetPermission = permissions.find((p: any) => p.pg === group.codeMapping);
    if (targetPermission) {
      switch (action) {
        case PermissionActionCode.CANCEL:
          return targetPermission.pl === PermissionActionCode.CANCEL;
        case PermissionActionCode.EDIT:
          return targetPermission.pl === PermissionActionCode.CANCEL || targetPermission.pl === PermissionActionCode.EDIT;
        case PermissionActionCode.READ:
          return targetPermission.pl === PermissionActionCode.CANCEL || targetPermission.pl === PermissionActionCode.EDIT || targetPermission.pl === PermissionActionCode.READ;
      }
    }
    return false;
  }
  return false;
};

export interface DisplayComponents { permissionLevelId: number, label: string, color: string; };
export const TranslatePermissionIDsToLabel = (group: PermissionGroupId, permission: PermissionReqSource[]): DisplayComponents => {
  let permissionByGroup = permission.filter((p: PermissionReqSource) => p.permissionGroupId == group);
  if (permissionByGroup.length > 0) {
    if (permissionByGroup.find((g: any) => g.permissionLevelId == PermissionLevelId.READ_EDIT_CANCEL))
      return { permissionLevelId: PermissionLevelId.READ_EDIT_CANCEL, label: "Read, Edit & Cancel", color: "text-green-500" };
    if (permissionByGroup.find((g: any) => g.permissionLevelId == PermissionLevelId.READ_EDIT))
      return { permissionLevelId: PermissionLevelId.READ_EDIT, label: "Read & Edit", color: "text-sky-600" };
    if (permissionByGroup.find((g: any) => g.permissionLevelId == PermissionLevelId.READ_ONLY))
      return { permissionLevelId: PermissionLevelId.READ_ONLY, label: "Read Only", color: "text-orange-400" };
    return { permissionLevelId: PermissionLevelId.INACTIVE, label: "Inactive", color: "text-red-400" };
  }
  return { permissionLevelId: PermissionLevelId.INACTIVE, label: "Inactive", color: "text-red-400" };
};


export const calculateTotalPrice = (membershipPlan: any, cpdPlan: any, discounts: number | null | undefined, hasJoiningFee: boolean = false) => {
  const { rootStore } = useStores();

  if (isDevelopment) {
    rootStore.notify("DEPRECATED FUNCTION: calculateTotalPrice", "warning");
  }

  const basePrice = hasJoiningFee ? 100 : 0;
  if (membershipPlan && cpdPlan) {
    return basePrice + membershipPlan.amount + +(cpdPlan.amount * (1 - (discounts || 0) * 0.01)).toFixed(2);
  } else if (membershipPlan) {
    return basePrice + membershipPlan.amount;
  } else if (cpdPlan) {
    return basePrice + cpdPlan.amount;
  }
  return 0;
}

export const GetJumpPath = () => {
  if (HasPermission(PermissionActionCode.READ, PermissionGroupId.CPD_MANAGEMENT)) return '/cpd';
  else if (HasPermission(PermissionActionCode.READ, PermissionGroupId.MEMBER_MANAGEMENT) || HasPermission(PermissionActionCode.READ, PermissionGroupId.PAYMENT_MANAGEMENT)) return '/members';
  return '/news';
}

export const GetExcelColumnName = (columnNumber: number): string => {
  let dividend = columnNumber;
  let columnName = '';

  while (dividend > 0) {
    const modulo = (dividend - 1) % 26;
    columnName = String.fromCharCode(65 + modulo) + columnName;
    dividend = Math.floor((dividend - 1) / 26);
  }

  return columnName;
};

// export const IsVersionOutdated = (currentVersion: string, latestVersion: string) => {
//   // if (!currentVersion) return true;
//   if (!currentVersion || !latestVersion) return false;

//   const [majorCurrent, minorCurrent, patchCurrent] = currentVersion.split('.').map(Number);
//   const [majorLatest, minorLatest, patchLatest] = latestVersion.split('.').map(Number);

//   if (majorCurrent < majorLatest) return true;
//   if (minorCurrent < minorLatest) return true;
//   if (patchCurrent < patchLatest) return true;

//   return false;
// }


export const GetPaymentMethodText = (paymentMethodId: PaymentMethodCategory) => {
  return GetOptionText(PaymentMethodTypes, paymentMethodId);
}

export const GetPaymentLogChangeKeyText = (key: PaymentLogChangeKey) => {
  if (key == PaymentLogChangeKey.CREATED) return "Payment Created";
  else if (key == PaymentLogChangeKey.UPDATED) return "Payment Updated";
  else if (key == PaymentLogChangeKey.APPROVED) return "Payment Approved";
  else if (key == PaymentLogChangeKey.REJECTED) return "Payment Rejected";
  else if (key == PaymentLogChangeKey.CANCELLED) return "Payment Cancelled";
  else if (key == PaymentLogChangeKey.RESTORE) return "Payment Restored";
  return "--";
}

export const DATE_MIN = "1900-01-01";
export const DATE_MAX = "3999-12-31";

export const TitleToGender = (title: string) => {
  if (!title) return "Male";
  const processed = title.toLowerCase();
  if (processed.includes("ms")) return "Female";
  if (processed.includes("mrs")) return "Female";
  if (processed.includes("miss")) return "Female";
  if (processed.includes("mr")) return "Male";
  return "Male";
}

export const IsCPDAddOnValid = (contact: AccountDetails | null | undefined, cpdTime: string = "") => {
  if (REMOVE_CPD_ADD_ON) return false;
  if (!contact) return false;

  const startDateStr = contact.cpdPackageStartDate;
  const endDateStr = contact.cpdPackageExpriedDate;
  if (!startDateStr || !endDateStr) return false;

  const startDate = moment(startDateStr).startOf('day');
  const endDate = moment(endDateStr).endOf('day');

  const isValid = moment(cpdTime).isBetween(startDate, endDate, null, '[]');
  return isValid;
}

export const FormatPrice = (price: number | string | null | undefined) => {
  if (IsNullOrUndefined(price)) return "--";

  return `${(+(+price!).toFixed(2)).toLocaleString()}`;
}


export const GetCPDLanguageContent = (rawString: string | CPDContentDictionary | undefined, language: Language = Language.ENGLISH) => {
  if (!rawString) return "--";

  if (typeof rawString !== "string") {
    let cnContent = "";
    let enContent = "";
    if (rawString["zh-CN"]) cnContent = rawString["zh-CN"];
    if (rawString["en-AU"]) enContent = rawString["en-AU"];

    return rawString[language] ?? enContent ?? cnContent ?? "--";
  }

  if (!rawString.startsWith("{")) return rawString;
  const parsed = JSON.parse(rawString);
  if (!parsed) return "--";

  return parsed[language] ?? parsed[Language.ENGLISH] ?? parsed[Language.ZH_CN] ?? "--";
}



export const GetCPDQuestionOptionText = (option: CPDQuestionOption | null | undefined, language: Language = Language.ENGLISH) => {
  if (!option) return "--";

  if (language === Language.ENGLISH) return option.enOption || option.cnOption || option.option || "--";
  if (language === Language.ZH_CN) return option.cnOption || option.enOption || option.option || "--";

  return option.enOption || option.cnOption || option.option || "--"
}


export const GetCPDQuestionText = (question: CPDQuestion | null | undefined, language: Language = Language.ENGLISH) => {
  if (!question) return "--";

  if (language === Language.ENGLISH) return question.enQuestion || question.cnQuestion || question.question || "--";
  if (language === Language.ZH_CN) return question.cnQuestion || question.enQuestion || question.question || "--";

  return question.enQuestion || question.cnQuestion || question.question || "--";
}


export const GetInvoiceAttachmentName = (invoice: Invoice | undefined | null) => {
  if (!invoice) return "";
  const ending = invoice.recipientName || invoice.description;
  return `Invoice #${invoice.id}${ending ? " - " + ending : ""}.pdf`;
}

export const GetWeekdayName = (dayOfWeek: number) => {
  return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dayOfWeek];
}

export const fileToArrayBuffer = (file: File): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = error => reject(error);
  });
};

export const StripHTML = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
}

export const GetClinicAddress = (clinic: ClinicInfo) => {
  return `${clinic.addressLine1}${clinic.addressLine2 ? ` ${clinic.addressLine2}` : ""}, ${clinic.suburb + ", " + clinic.state + ", " + clinic.postcode}`
}

export const GetFriendlyDate = (date: string | Date | undefined | null) => {
  if (!date) return "--";
  if (moment(date).isSame(moment(), "day")) return "Today";
  if (moment(date).isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
  if (moment(date).isSame(moment().add(1, "day"), "day")) return "Tomorrow";

  // IF SAME WEEK
  if (moment(date).isSame(moment(), "isoWeek")) return moment(date).format("ddd");

  // IF LAST WEEK
  if (moment(date).isSame(moment().subtract(1, "week"), "isoWeek")) return `Last ${moment(date).format("ddd")}`;

  return moment(date).format(AUStrDateFormat);
}

export const GetFriendlyFileSize = (size: number) => {
  if (size < 1024) return `${size} bytes`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

export const GetCurrentCPDYear = () => {
  const currentMonth = moment().month() + 1;
  if (currentMonth >= 12) {
    return moment().year() + 1;
  } else {
    return moment().year();
  }
}

export const GetCPDYearRange = (year: number) => {
  return {
    year,
    dateFrom: moment().year(year - 1).month(11).date(1).format(AUStrDateFormat),
    dateTo: moment().year(year).month(10).date(30).format(AUStrDateFormat)
  }
}

export const HashString = (str: string) => {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(str)).then((hashBuffer) => {
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  });
}

export interface BannerItem {
  url: string;
  title?: string;
  description?: string;
  alignment?: {
    horizontal: 'left' | 'center' | 'right',
    vertical: 'top' | 'center' | 'bottom'
  };
  textColor?: string;
}

export const getAlignmentStyles = (alignment?: BannerItem['alignment'], shrinkMode: boolean = false) => {
  const styles: any = { position: 'absolute' };

  // Default to bottom-left if no alignment
  if (!alignment) {
    return {
      bottom: '3rem',
      left: '3rem',
      textAlign: 'left'
    };
  }

  switch (alignment.vertical) {
    case 'top': styles.top = shrinkMode ? '0.2rem' : '2rem'; break;
    case 'center': styles.top = '50%'; styles.transform = 'translateY(-50%)'; break;
    case 'bottom': styles.bottom = shrinkMode ? '0.2rem' : '3rem'; break;
  }

  switch (alignment.horizontal) {
    case 'left': styles.left = shrinkMode ? '0.2rem' : '3rem'; styles.textAlign = 'left'; break;
    case 'center':
      styles.left = '50%';
      styles.textAlign = 'center';
      if (styles.transform) styles.transform += ' translateX(-50%)';
      else styles.transform = 'translateX(-50%)';
      break;
    case 'right': styles.right = shrinkMode ? '0.2rem' : '3rem'; styles.textAlign = 'right'; break;
  }

  return styles;
};

export const GetPaymentCategory = (paymentCategory: PaymentCategory) => {
  return paymentCategory === PaymentCategory.MEMBERSHIP_FEE ? t("MEMBERSHIP_PAYMENT")
    : paymentCategory === PaymentCategory.CPD ? t("CPD_ACTIVITY")
      : "";
}

export const GetCPDCategoryText = (cpdCategoryId: CPDCategory) => {
  return cpdCategoryId === CPDCategory.EVENT ? 'Event'
    : cpdCategoryId === CPDCategory.PUBLIC_CPD ? 'CPD Activity'
      : cpdCategoryId === CPDCategory.CPD_PACKAGE ? 'CPD Package'
        : cpdCategoryId === CPDCategory.TRAINING_COURSE ? 'Training Course'
          : cpdCategoryId === CPDCategory.SUMMIT ? 'Summit'
            : cpdCategoryId === CPDCategory.PAID_EVENT ? 'Paid Event'
              : "";
}

export const TryGetLanguageContent = (savedString: string) => {
  if (!savedString) return DeepCopy(EmptyLanguageSet);

  let parsed: LanagugeContentSet = EmptyLanguageSet;
  try {
    parsed = JSON.parse(savedString);
  } catch (err) {
    return EmptyLanguageSet
  }

  let newObejct = DeepCopy(EmptyLanguageSet);

  newObejct[Language.ENGLISH] = parsed[Language.ENGLISH] ?? "";
  newObejct[Language.ZH_CN] = parsed[Language.ZH_CN] ?? "";

  return newObejct;
}


export const CalculateSummitPrice = (summitInfo: CPDActivity, config: {
  includeDinner: boolean,
  freeSummit: boolean,
  isMember: boolean
}) => {
  const { includeDinner, freeSummit, isMember } = config;

  let summitPrice = summitInfo.pricing;
  let dinnerPrice = summitInfo.galaPriceGuest;

  if (isMember == true) {
    summitPrice = summitInfo.memberPrice;
    dinnerPrice = summitInfo.galaPriceMember;
  }

  if (freeSummit) summitPrice = 0;

  return summitPrice + (includeDinner ? dinnerPrice : 0);
}

export const GetFileNameAndExtension = (fileName: string) => {
  let result = {
    name: "",
    extension: ""
  };
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    result.name = fileName;
    result.extension = "";
  } else {
    result.name = fileName.substring(0, lastDotIndex);
    result.extension = fileName.substring(lastDotIndex + 1);
  }
  return result;
}